// AIMS - in-app QR scanner (Offline Inspection mode).
//
// Replaces the removed, flaky html5-qrcode scanner. Built on `qr-scanner`
// (Nimiq): tiny, uses the native BarcodeDetector fast-path where available
// (Android) and an inlined WASM worker fallback (iOS PWAs). We own the UI so it
// matches the redesign, unlike html5-qrcode's built-in widget.
//
// Reusable: used from the asset search panel now, and the site-visit inspection
// screen in Phase 6. onResult receives { assetNo, catNo, raw } parsed from the
// scanned tag (tags are full URLs like `…/?assetNo=XXX`); the caller decides what
// to do (navigate online, or resolve against the IndexedDB pool offline).

import React, { useEffect, useRef, useState, useCallback } from 'react';
import QrScanner from 'qr-scanner';
import Dialog from '@mui/material/Dialog';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import FlashlightOnIcon from '@mui/icons-material/FlashlightOn';
import FlashlightOffIcon from '@mui/icons-material/FlashlightOff';
import CameraswitchIcon from '@mui/icons-material/Cameraswitch';
import HistoryIcon from '@mui/icons-material/History';
import { recordScan } from '../../offline/scanHistory';
import ScanHistoryDialog from './ScanHistoryDialog';

// Parse a scanned QR into asset identifiers + a history-friendly summary. Tags
// carry a full URL `https://asset.1maintenance.com.au/?assetNo=XXX` (assetNo =
// asset.assetId, catNo = asset.catNo). Falls back to treating a bare/damaged tag
// as an assetNo so it still resolves. (Mirrors the playbook §8 idFromScan.)
//   idType: 'a' = assetId, 'c' = catNo, '?' = a URL carrying neither.
//   value : the identifying value for that type (what we log / filter on).
export function parseScan(text) {
  const raw = (text || '').trim();
  let assetNo = null;
  let catNo = null;
  try {
    const u = new URL(raw);
    assetNo = u.searchParams.get('assetNo') || null;
    catNo = u.searchParams.get('catNo') || null;
  } catch {
    // Not a URL - treat the whole payload as an asset number.
    assetNo = raw || null;
  }
  let idType = '?';
  let value = raw;
  if (assetNo) { idType = 'a'; value = assetNo; }
  else if (catNo) { idType = 'c'; value = catNo; }
  return { assetNo, catNo, raw, idType, value };
}

export default function QrScanDialog({ open, onClose, onResult, title = 'Scan asset QR' }) {
  const videoRef = useRef(null);
  const scannerRef = useRef(null);
  const handledRef = useRef(false); // guard: fire onResult/record once per scan
  const [error, setError] = useState(null);
  const [hasFlash, setHasFlash] = useState(false);
  const [flashOn, setFlashOn] = useState(false);
  const [multiCamera, setMultiCamera] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const stop = useCallback(() => {
    const s = scannerRef.current;
    if (s) {
      try { s.stop(); } catch (_) {}
      try { s.destroy(); } catch (_) {}
      scannerRef.current = null;
    }
  }, []);

  const handleClose = useCallback(() => {
    stop();
    if (onClose) onClose();
  }, [stop, onClose]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    handledRef.current = false; // fresh scan session
    setError(null);
    setFlashOn(false);

    // Guard: no camera / insecure context (getUserMedia needs https or localhost).
    QrScanner.hasCamera()
      .then((present) => {
        if (cancelled) return;
        if (!present) { setError('No camera found on this device.'); return; }
        const video = videoRef.current;
        if (!video) return;

        const scanner = new QrScanner(
          video,
          async (result) => {
            // First good decode wins - stop immediately and guard so we neither
            // fire onResult twice nor double-log the same scan.
            if (cancelled || handledRef.current) return;
            handledRef.current = true;
            stop();
            const parsed = parseScan(result && result.data ? result.data : result);
            // Log EVERY scan (resolved or not) BEFORE handing back - awaited so the
            // write is durable even when onResult navigates away immediately.
            await recordScan({ idType: parsed.idType, value: parsed.value, raw: parsed.raw });
            if (onResult) onResult(parsed);
          },
          {
            preferredCamera: 'environment',
            highlightScanRegion: true,
            highlightCodeOutline: true,
            maxScansPerSecond: 8,
            returnDetailedScanResult: true,
          }
        );
        scannerRef.current = scanner;

        scanner
          .start()
          .then(async () => {
            if (cancelled) { stop(); return; }
            try { setHasFlash(await scanner.hasFlash()); } catch (_) { setHasFlash(false); }
            try {
              const cams = await QrScanner.listCameras(true);
              setMultiCamera(Array.isArray(cams) && cams.length > 1);
            } catch (_) { setMultiCamera(false); }
          })
          .catch((err) => {
            if (cancelled) return;
            const name = err && err.name;
            if (name === 'NotAllowedError') setError('Camera permission denied. Allow camera access in your browser settings, then try again.');
            else if (name === 'NotFoundError') setError('No camera found on this device.');
            else setError('Could not start the camera. ' + (err && err.message ? err.message : ''));
          });
      })
      .catch(() => { if (!cancelled) setError('Could not access the camera.'); });

    return () => { cancelled = true; stop(); };
  }, [open, stop, onResult]);

  const toggleFlash = async () => {
    const s = scannerRef.current;
    if (!s) return;
    try {
      await s.toggleFlash();
      setFlashOn(s.isFlashOn());
    } catch (_) { /* flash not controllable - ignore */ }
  };

  const switchCamera = async () => {
    const s = scannerRef.current;
    if (!s) return;
    try {
      const cams = await QrScanner.listCameras(true);
      if (!cams || cams.length < 2) return;
      // Cycle to the next camera relative to the active one is non-trivial; simplest
      // reliable toggle is front<->back via facingMode.
      const current = s._preferredCamera; // internal, best-effort
      const next = current === 'user' ? 'environment' : 'user';
      await s.setCamera(next);
      try { setHasFlash(await s.hasFlash()); } catch (_) {}
    } catch (_) { /* ignore */ }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs"
      PaperProps={{ sx: { background: '#000', borderRadius: 'var(--r, 12px)', overflow: 'hidden', m: { xs: 1, sm: 2 } } }}>
      <Box sx={{ position: 'relative', width: '100%' }}>
        {/* Header bar over the video */}
        <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 2,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 1, py: 0.75, background: 'linear-gradient(to bottom, rgba(0,0,0,0.6), rgba(0,0,0,0))' }}>
          <Box sx={{ color: '#fff', fontSize: 14, fontWeight: 600, pl: 1 }}>{title}</Box>
          <Box>
            <IconButton size="small" onClick={() => setHistoryOpen(true)} aria-label="Scan history" sx={{ color: '#fff' }}>
              <HistoryIcon fontSize="small" />
            </IconButton>
            {multiCamera && (
              <IconButton size="small" onClick={switchCamera} aria-label="Switch camera" sx={{ color: '#fff' }}>
                <CameraswitchIcon fontSize="small" />
              </IconButton>
            )}
            {hasFlash && (
              <IconButton size="small" onClick={toggleFlash} aria-label="Toggle torch" sx={{ color: '#fff' }}>
                {flashOn ? <FlashlightOnIcon fontSize="small" /> : <FlashlightOffIcon fontSize="small" />}
              </IconButton>
            )}
            <IconButton size="small" onClick={handleClose} aria-label="Close scanner" sx={{ color: '#fff' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {error ? (
          <Box sx={{ p: 4, textAlign: 'center', color: '#fff', minHeight: 240, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 2 }}>
            <Box sx={{ fontSize: 14, lineHeight: 1.5 }}>{error}</Box>
            <Button variant="contained" onClick={handleClose} sx={{ alignSelf: 'center', textTransform: 'none' }}>Close</Button>
          </Box>
        ) : (
          <Box sx={{ position: 'relative', width: '100%', aspectRatio: '1 / 1', background: '#000' }}>
            {/* qr-scanner attaches the stream + its own highlight overlay to this video */}
            <video ref={videoRef} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} muted playsInline />
          </Box>
        )}

        {!error && (
          <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 2, px: 2, py: 1.25,
            textAlign: 'center', color: 'rgba(255,255,255,0.85)', fontSize: 12,
            background: 'linear-gradient(to top, rgba(0,0,0,0.6), rgba(0,0,0,0))' }}>
            Point the camera at the asset's QR tag
          </Box>
        )}
      </Box>

      {/* Recent scans - selecting one replays it through onResult, so it behaves
          exactly like scanning that code again in this context (search → opens
          the asset; inspection → fills the filter). Returning true marks it
          handled so ScanHistoryDialog skips its own default navigation. */}
      <ScanHistoryDialog
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        onSelect={(entry) => {
          setHistoryOpen(false);
          if (!onResult) return false; // let the dialog fall back to opening the asset
          const assetNo = entry.idType === 'a' ? entry.value : null;
          const catNo = entry.idType === 'c' ? entry.value : null;
          stop();
          onResult({ assetNo, catNo, raw: entry.raw, idType: entry.idType, value: entry.value });
          return true;
        }}
      />
    </Dialog>
  );
}
